/**
 * Provider card for a Google Drive / OneDrive / SharePoint file — used both for
 * cloud-link attachments (source: 'cloud_link') and for Drive/SharePoint URLs
 * pasted into a message. It only ever links to the provider; it never calls the
 * S3 download path, because there are no bytes on our side.
 */

import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Info, X } from 'lucide-react';
import type { CloudFileProvider } from '@/services/messagingApi';
import { cloudFilesApi } from '@/services/cloudFilesApi';
import useActiveOrgId from '@/hooks/useActiveOrgId';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import { toSafeHref } from '../linkify';
import { PROVIDER_LABEL, type CloudFileKind } from './cloudLinks';
import { FileKindIcon, ProviderMark, formatBytes } from './CloudIcons';

export function useCloudKindLabel() {
  const { t } = useAppTranslation();
  return (kind: CloudFileKind): string => {
    switch (kind) {
      case 'document':
        return t('messaging.cloud.kindDocument', 'Document');
      case 'spreadsheet':
        return t('messaging.cloud.kindSpreadsheet', 'Spreadsheet');
      case 'presentation':
        return t('messaging.cloud.kindPresentation', 'Presentation');
      case 'form':
        return t('messaging.cloud.kindForm', 'Form');
      case 'drawing':
        return t('messaging.cloud.kindDrawing', 'Drawing');
      case 'pdf':
        return t('messaging.cloud.kindPdf', 'PDF');
      case 'image':
        return t('messaging.cloud.kindImage', 'Image');
      case 'video':
        return t('messaging.cloud.kindVideo', 'Video');
      case 'archive':
        return t('messaging.cloud.kindArchive', 'Archive');
      case 'folder':
        return t('messaging.cloud.kindFolder', 'Folder');
      default:
        return t('messaging.cloud.kindFile', 'File');
    }
  };
}

interface CloudFileCardProps {
  provider: CloudFileProvider;
  kind: CloudFileKind;
  /** File name; falls back to "<Provider> <kind>". */
  name?: string | null;
  href: string | null | undefined;
  sizeBytes?: number | null;
  /** Show "People in this conversation need access…" (sender's own attachment). */
  showAccessNote?: boolean;
  /**
   * A sent cloud-link attachment: load its thumbnail AS THE VIEWER (their own
   * Drive / OneDrive & SharePoint access in this org). Pasted links have none.
   */
  attachmentId?: number;
  onDismiss?: () => void;
  compact?: boolean;
  className?: string;
  testId?: string;
}

export function CloudFileCard({
  provider,
  kind,
  name,
  href,
  sizeBytes,
  showAccessNote,
  attachmentId,
  onDismiss,
  compact,
  className,
  testId = 'cloud-file-card',
}: CloudFileCardProps) {
  const { t } = useAppTranslation();
  const kindLabel = useCloudKindLabel();
  const providerLabel = PROVIDER_LABEL[provider];
  const safeHref = toSafeHref(href || '');
  const title = name?.trim() || `${providerLabel} ${kindLabel(kind).toLowerCase()}`;
  const size = formatBytes(sizeBytes);
  const openLabel = t('messaging.cloud.openIn', 'Open in {{provider}}', { provider: providerLabel });

  return (
    <div
      data-testid={testId}
      data-provider={provider}
      aria-disabled={!safeHref || undefined}
      className={cn(
        'relative mt-2 max-w-sm rounded-md border border-slate-200 bg-white text-left text-slate-900 shadow-sm',
        'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100',
        compact ? 'p-2' : 'p-2.5',
        !safeHref && 'opacity-70',
        className,
      )}
    >
      {attachmentId != null && !compact && safeHref && (
        <CloudFilePreview
          part="image"
          attachmentId={attachmentId}
          href={safeHref}
          title={title}
          providerLabel={providerLabel}
          connectLabel={provider === 'google_drive' ? 'Google Drive' : 'OneDrive & SharePoint'}
        />
      )}
      <div className="flex items-start gap-2.5">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          <FileKindIcon kind={kind} className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
            <ProviderMark provider={provider} className="h-3.5 w-3.5" />
            <span className="truncate">{providerLabel}</span>
          </div>
          {safeHref ? (
            <a
              href={safeHref}
              target="_blank"
              rel="noopener noreferrer"
              className="block truncate text-sm font-semibold text-blue-700 hover:underline dark:text-blue-300"
              onClick={(e) => e.stopPropagation()}
              title={title}
            >
              {title}
            </a>
          ) : (
            <span className="block truncate text-sm font-semibold">{title}</span>
          )}
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {[kindLabel(kind), size].filter(Boolean).join(' • ')}
          </div>
          {safeHref && (
            <a
              href={safeHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-xs text-blue-700 hover:underline dark:text-blue-300"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3 w-3" />
              {openLabel}
            </a>
          )}
          {!safeHref && (
            <div className="mt-1 text-xs text-amber-700 dark:text-amber-400" data-testid="cloud-link-unavailable">
              {t('messaging.cloud.linkUnavailable', 'This link is no longer available.')}
            </div>
          )}
        </div>
      </div>
      {attachmentId != null && !compact && safeHref && (
        <CloudFilePreview
          part="hint"
          attachmentId={attachmentId}
          href={safeHref}
          title={title}
          providerLabel={providerLabel}
          connectLabel={provider === 'google_drive' ? 'Google Drive' : 'OneDrive & SharePoint'}
        />
      )}
      {showAccessNote && safeHref && (
        <p className="mt-2 flex items-start gap-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400">
          <Info className="mt-px h-3 w-3 flex-shrink-0" />
          {t('messaging.cloud.accessNote', 'People in this conversation need access in {{provider}} to open it.', {
            provider: providerLabel,
          })}
        </p>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="absolute right-1 top-1 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
          title={t('messaging.linkPreview.dismiss', 'Remove preview')}
          aria-label={t('messaging.linkPreview.dismiss', 'Remove preview')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export default CloudFileCard;

/**
 * The file's thumbnail, fetched with the VIEWER's own access (TRUS-638): it only
 * appears for people the provider lets see the file, exactly like Teams. Anyone
 * else keeps the plain card, plus a hint when connecting would help.
 */
function CloudFilePreview({
  part,
  attachmentId,
  href,
  title,
  providerLabel,
  connectLabel,
}: {
  /** The image goes above the file row, the "why not" hint below it (same cached query). */
  part: 'image' | 'hint';
  attachmentId: number;
  href: string | null;
  title: string;
  providerLabel: string;
  /** The connection to make: "Google Drive" or "OneDrive & SharePoint". */
  connectLabel: string;
}) {
  const { t } = useAppTranslation();
  const orgId = useActiveOrgId();
  const preview = useQuery({
    // Per viewer AND org: the grant (and so the answer) differs by org.
    queryKey: ['cloud-file-preview', orgId, attachmentId],
    queryFn: () => cloudFilesApi.getAttachmentPreview(attachmentId),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const blob = preview.data && preview.data.available === true ? preview.data.blob : null;
  const [src, setSrc] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    setSrc(url);
    setBroken(false);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  if (part === 'hint') {
    if (preview.isLoading || (src && !broken)) return null;
  } else if (preview.isLoading) {
    return <div className="mb-2 h-32 w-full animate-pulse rounded bg-slate-100 dark:bg-slate-800" data-testid="cloud-preview-loading" />;
  }
  if (part === 'image') {
    if (!src || broken) return null;
    const img = (
      <img
        src={src}
        alt={t('messaging.cloud.previewAlt', 'Preview of {{name}}', { name: title })}
        className="max-h-56 w-full rounded border border-slate-200 bg-slate-50 object-contain dark:border-slate-700 dark:bg-slate-800"
        onError={() => setBroken(true)}
        data-testid="cloud-preview-image"
      />
    );
    return (
      <div className="mb-2">
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
            {img}
          </a>
        ) : (
          img
        )}
      </div>
    );
  }
  const data = preview.data;
  const reason = data && data.available === false ? data.reason : null;
  if (reason === 'not_connected' || reason === 'no_access') {
    return (
      <p className="mt-2 text-[11px] leading-snug text-slate-500 dark:text-slate-400" data-testid={`cloud-preview-${reason}`}>
        {reason === 'not_connected'
          ? t('messaging.cloud.previewConnect', 'Connect {{provider}} in Settings → Connected services to see a preview.', {
              provider: connectLabel,
            })
          : t('messaging.cloud.previewNoAccess', 'No preview: {{provider}} hasn’t given you access to this file.', {
              provider: providerLabel,
            })}
      </p>
    );
  }
  return null;
}
